import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function MoodBasedRestaurantFinderPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Mood Based Restaurant Finder — Match Your Mood to the Perfect Meal"
        description="Not sure what you're craving? Tell Hangrily your mood and it finds restaurants that match how you feel. Adventurous, cozy, celebratory, or healthy — your mood picks your food."
        path="/mood-based-restaurant-finder"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Mood Based Restaurant Finder — Feelings to Food Matching',
            description: 'Discover restaurants based on your mood. Adventurous, comfort, healthy, celebratory — match how you feel to what you eat.',
            url: 'https://hangrily.app/mood-based-restaurant-finder'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Mood Based Restaurant Finder', item: 'https://hangrily.app/mood-based-restaurant-finder' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Is there a restaurant app that matches your mood?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "Yes. Hangrily's mood-based restaurant finder lets you pick how you're feeling — adventurous, comfort, healthy, celebratory, quick, or social — and surfaces restaurants that match your emotional state, not just your cuisine preferences."
                }
              },
              {
                '@type': 'Question',
                name: 'How does mood-based restaurant picking work?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "You select your mood before swiping: Adventurous (try something new), Comfort (familiar and cozy), Healthy (clean eating), Celebratory (special occasion), Quick (fast and convenient), or Social (lively atmosphere). Hangrily then prioritizes restaurants whose vibe, cuisine, and atmosphere align with that mood."
                }
              },
              {
                '@type': 'Question',
                name: "What should I eat based on my mood?",
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "Feeling adventurous? Try a new cuisine you've never had. Need comfort? Go for familiar flavors and cozy atmospheres. Being healthy? Look for fresh, nutritious options. Celebrating? Pick somewhere with great ambiance and shareable plates. In a rush? Fast-casual with quality ingredients. Want social? Lively spots with communal seating or great bars."
                }
              },
              {
                '@type': 'Question',
                name: 'Can I filter restaurants by mood and cuisine together?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. Hangrily lets you combine mood filters with cuisine, price, and distance preferences. So you can search for "adventurous Thai under $20 within 10 minutes" or "cozy Italian for date night." The more specific you are, the better the matches.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Mood Based Restaurant Finder</h1>
          <p className="hero-subtitle">
            You don't always know what you want. But you always know how you feel.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>When You Don't Know What You Want — But You Know How You Feel</h2>
              <p>
                "What do you want to eat?" is the wrong question. The right question is: <strong>"How do 
                you feel?"</strong> Because your mood predicts what you'll enjoy way better than your 
                cravings do. When you're stressed, you don't want experimental molecular gastronomy — 
                you want something familiar and comforting. When you're celebrating, you want atmosphere 
                and shareable plates, not a drive-thru window.
              </p>
              <p>
                Hangrily's mood-based restaurant finder skips the "what cuisine?" question and starts with 
                "how do you feel?" Then it matches your emotional state to real nearby restaurants that 
                fit <em>both</em> your mood and your practical needs. It's the difference between asking 
                a random wheel and asking a friend who actually knows you.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Find Food for Your Mood →
              </button>
            </div>
            <div className="column-visual">
              <div className="stat-highlight-grid">
                <div className="stat-highlight">
                  <span className="stat-number-lg">6</span>
                  <span className="stat-label-sm">Mood filters: adventurous, comfort, healthy, celebratory, quick, social</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">15+</span>
                  <span className="stat-label-sm">Restaurant options matched to your mood per session</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">2 min</span>
                  <span className="stat-label-sm">From "I don't know" to "let's go"</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">100%</span>
                  <span className="stat-label-sm">Real restaurants, not random suggestions</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">6 Moods, Infinite Restaurant Matches</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">🚀</div>
              <h3>Adventurous</h3>
              <p>
                You're in the mood to try something new. Cuisines you've never had. Restaurants off the 
                beaten path. Dishes you can't pronounce. Hangrily prioritizes ethnic gems, chef-driven 
                spots, and places with unique menus. Perfect for foodies, travelers, or anyone bored with 
                their usual rotation.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🛋️</div>
              <h3>Comfort</h3>
              <p>
                It's been a long day and you need something familiar, warm, and satisfying. Think cozy 
                diners, classic Italian, soulful BBQ, or your neighborhood pub. Hangrily surfaces restaurants 
                with hearty portions, welcoming atmospheres, and flavors that feel like a hug. No surprises. 
                Just comfort.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🥗</div>
              <h3>Healthy</h3>
              <p>
                You're feeling disciplined and want something that nourishes you. Fresh salads, grain 
                bowls, lean proteins, and plant-based options. Hangrily prioritizes restaurants with 
                healthy menus, nutritional transparency, and light preparations. You don't have to 
                sacrifice flavor for health — these spots do both.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🎉</div>
              <h3>Celebratory</h3>
              <p>
                Something worth celebrating — promotion, anniversary, birthday, or just because. You 
                want ambiance, presentation, and a sense of occasion. Hangrily surfaces restaurants with 
                elevated dining rooms, craft cocktails, shareable plates, and stellar service. This is 
                not a Tuesday night — this is a memory.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">⚡</div>
              <h3>Quick</h3>
              <p>
                You're hungry NOW and don't have 45 minutes for a sit-down experience. But "quick" 
                doesn't have to mean "bad." Hangrily finds fast-casual spots with quality ingredients, 
                food trucks with cult followings, and counter-service gems that serve incredible food in 
                under 10 minutes. Fast can still be delicious.
              </p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🍻</div>
              <h3>Social</h3>
              <p>
                You want a lively atmosphere, great drinks, and a place where conversation flows. 
                Think gastropubs, wine bars, breweries with food menus, and restaurants with communal 
                seating. Hangrily prioritizes spots with strong bar programs, shareable appetizers, and 
                energetic vibes. Dinner is just the excuse — the experience is the point.
              </p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">How Mood Matching Works</h2>
          <div className="numbered-steps">
            <div className="numbered-step">
              <div className="step-num">1</div>
              <div className="step-content">
                <h3>Pick Your Mood</h3>
                <p>
                  Before you even think about cuisine, select how you're feeling. Adventurous? Comfort? 
                  Healthy? Celebratory? Quick? Social? Your mood becomes the primary filter — because 
                  how you feel determines what you'll enjoy far more than what you "should" want.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">2</div>
              <div className="step-content">
                <h3>Add Practical Filters</h3>
                <p>
                  Layer on cuisine preferences, price range, and distance. So you're not just getting 
                  "adventurous restaurants" — you're getting "adventurous Thai places under $25 within 
                  15 minutes." The more specific, the better the match.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">3</div>
              <div className="step-content">
                <h3>Swipe Mood-Matched Restaurants</h3>
                <p>
                  Hangrily shows you one restaurant at a time, prioritized by how well it matches your 
                  mood AND your practical needs. Each card includes photos, ratings, price level, distance, 
                  and why it fits your mood. Swipe yes, no, or love. The algorithm finds your match in 
                  about 2 minutes.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Mood + Cuisine Combinations People Love</h2>
          <div className="comparison-grid">
            <div className="comparison-item win">
              <h3>🚀 Adventurous + Japanese</h3>
              <p>Ramen shops you've never tried, omakase spots, or izakayas with small plates. Perfect when you want to explore.</p>
            </div>
            <div className="comparison-item win">
              <h3>🛋️ Comfort + Italian</h3>
              <p>Homemade pasta, wood-fired pizza, and red sauce joints that feel like family dinners. The ultimate comfort combo.</p>
            </div>
            <div className="comparison-item win">
              <h3>🥗 Healthy + Mediterranean</h3>
              <p>Grain bowls, fresh hummus, grilled fish, and vegetables done right. Clean eating that actually tastes amazing.</p>
            </div>
            <div className="comparison-item win">
              <h3>🎉 Celebratory + Steakhouse</h3>
              <p>Dry-aged beef, craft cocktails, and white tablecloth service. When the occasion calls for something special.</p>
            </div>
            <div className="comparison-item win">
              <h3>⚡ Quick + Tacos</h3>
              <p>Authentic taquerias and fast-casual Mexican spots. Fast, flavorful, and universally loved. The ultimate quick fix.</p>
            </div>
            <div className="comparison-item win">
              <h3>🍻 Social + Gastropub</h3>
              <p>Craft beer, elevated bar food, and communal tables. Great for groups who want quality food without the formality.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is there a restaurant app that matches your mood?</h3>
              <p>
                Yes. Hangrily's mood-based restaurant finder lets you select how you're feeling — adventurous, 
                comfort, healthy, celebratory, quick, or social — and surfaces restaurants that match your 
                emotional state. It combines mood filtering with cuisine, price, and distance for precise matches.
              </p>
            </div>
            <div className="faq-item">
              <h3>How does mood-based restaurant picking work?</h3>
              <p>
                You pick a mood before swiping. Hangrily then prioritizes restaurants whose atmosphere, 
                cuisine, and vibe align with how you feel. A "comfort" mood surfaces cozy, familiar spots. 
                An "adventurous" mood surfaces unique, unfamiliar options. You still swipe yes/no/love, 
                but the pool is pre-filtered by emotion.
              </p>
            </div>
            <div className="faq-item">
              <h3>What should I eat based on my mood?</h3>
              <p>
                Adventurous moods want new cuisines and unique experiences. Comfort moods want familiar 
                flavors in cozy settings. Healthy moods want fresh, nutritious preparations. Celebratory 
                moods want ambiance and shareable plates. Quick moods want fast-casual quality. Social 
                moods want lively atmospheres with great drinks.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can I combine mood with cuisine filters?</h3>
              <p>
                Absolutely. Hangrily lets you stack mood filters with cuisine, price range, and distance. 
                So you can search for "comfort Italian under $30 within 20 minutes" or "adventurous street 
                food nearby." The more filters you add, the more precise your matches become.
              </p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Let Your Mood Pick Your Meal</h2>
          <p>Not sure what you want? Start with how you feel. Hangrily finds the perfect match.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Find Food for Your Mood →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
